import React from "react";
import styled from "styled-components";
import moment from "moment";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Mail,
  Phone,
  Shield,
  ShieldOff,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { useClientsStore } from "./use-clients-store";
import { ClientStatusBadge, getClientStatus } from "./client-status-badge";
import type { AuthClient } from "./clients-types";

const TableWrap = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  white-space: nowrap;
`;

const SortButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => (p.$active ? "#1976d2" : "#666")};
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
`;

const MainText = styled.div`
  color: #202020;
  font-weight: 600;
  line-height: 1.35;
`;

const SubText = styled.div`
  color: #777;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 2px;
  word-break: break-word;
`;

const ContactLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #202020;
  line-height: 1.4;
  white-space: nowrap;

  & + & {
    margin-top: 4px;
  }

  svg {
    flex: 0 0 auto;
    color: #777;
  }
`;

const ProviderList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 210px;
`;

const ProviderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #d8e3f4;
  border-radius: 999px;
  background: #f7faff;
  color: #24517a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 7px;
  text-transform: uppercase;
`;

const Muted = styled.span`
  color: #999;
`;

const ActionBtn = styled.button<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid ${(p) => p.$color}33;
  border-radius: 4px;
  background: transparent;
  color: ${(p) => p.$color};
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${(p) => p.$color}0d;
  }
  & + & {
    margin-left: 4px;
  }
`;

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  font-size: 13px;
  color: #666;
`;

const PageButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LimitSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  background: white;
  cursor: pointer;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 4px 10px;
  border: 1px solid ${(p) => (p.$active ? "#1976d2" : "#e0e0e0")};
  border-radius: 4px;
  background: ${(p) => (p.$active ? "#1976d2" : "white")};
  color: ${(p) => (p.$active ? "white" : "#333")};
  font-size: 12px;
  cursor: pointer;
  &:hover {
    border-color: #1976d2;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EmptyRow = styled.tr`
  td {
    padding: 40px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }
`;

function getClientName(client: AuthClient): string {
  const p = client.profile;
  if (p) {
    const parts = [p.last_name, p.first_name, p.middle_name].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
  }

  const providerName = client.oauth_providers?.find((provider) =>
    provider.full_name || provider.display_name || provider.first_name || provider.last_name
  );
  if (providerName) {
    const parts = [providerName.last_name, providerName.first_name].filter(Boolean);
    return providerName.full_name || parts.join(" ") || providerName.display_name || "—";
  }

  return client.public_login || client.email || "—";
}

function getPrimaryProvider(client: AuthClient) {
  return client.oauth_providers?.[0] || null;
}

function formatProviderIdentity(client: AuthClient): string {
  const provider = getPrimaryProvider(client);
  if (!provider) return client.public_login || "—";
  return provider.username || provider.display_name || provider.provider_user_id || client.public_login || "—";
}

function formatBirthdate(client: AuthClient): string | null {
  const provider = client.oauth_providers?.find((item) => item.birthdate);
  return provider?.birthdate ? moment(provider.birthdate).format("DD.MM.YYYY") : null;
}

function getAge(client: AuthClient): number | null {
  const provider = client.oauth_providers?.find((item) => item.birthdate);
  if (!provider?.birthdate) return null;

  const birthdate = moment(provider.birthdate);
  if (!birthdate.isValid()) return null;

  return moment().diff(birthdate, "years");
}

function formatGender(value?: string | null): string | null {
  switch (value) {
    case "male":
    case "m":
      return "муж.";
    case "female":
    case "f":
      return "жен.";
    default:
      return value || null;
  }
}

function getContactSource(client: AuthClient): string {
  if (client.contact_source) return client.contact_source;
  const provider = client.oauth_providers?.find((item) => item.email || item.phone_number);
  return provider?.provider || "account";
}

export const ClientsTable: React.FC = () => {
  const {
    clients,
    total,
    filter,
    loading,
    setFilter,
    fetchClients,
    setSelectedClient,
    setModalBlock,
    setModalDelete,
    unblockClient,
    restoreClient,
  } = useClientsStore();

  const totalPages = Math.ceil(total / filter.limit) || 1;

  const goToPage = (page: number) => {
    setFilter({ page });
    setTimeout(() => fetchClients(), 0);
  };

  const handleBlock = (client: AuthClient) => {
    setSelectedClient(client);
    setModalBlock(true);
  };

  const handleDelete = (client: AuthClient) => {
    setSelectedClient(client);
    setModalDelete(true);
  };

  const handleSort = (sortBy: string) => {
    const isSameColumn = filter.sort_by === sortBy;
    if (!isSameColumn) {
      setFilter({ sort_by: sortBy, sort_order: "asc", page: 1 });
      return;
    }

    if (filter.sort_order === "asc") {
      setFilter({ sort_by: sortBy, sort_order: "desc", page: 1 });
      return;
    }

    setFilter({ sort_by: "", sort_order: "desc", page: 1 });
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter({ limit: Number(e.target.value), page: 1 });
  };

  const renderSortIcon = (sortBy: string) => {
    const isDefaultCreatedAt = !filter.sort_by && sortBy === "created_at";
    if (filter.sort_by !== sortBy && !isDefaultCreatedAt) return <ChevronsUpDown size={13} />;
    const order = isDefaultCreatedAt ? "desc" : filter.sort_order;
    return order === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
  };

  const renderHeader = (label: string, sortBy: string) => (
    <SortButton
      type="button"
      $active={filter.sort_by === sortBy || (!filter.sort_by && sortBy === "created_at")}
      onClick={() => handleSort(sortBy)}
    >
      {label}
      {renderSortIcon(sortBy)}
    </SortButton>
  );

  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>{renderHeader("ФИО / логин", "name")}</Th>
            <Th>{renderHeader("Контакты", "email")}</Th>
            <Th>{renderHeader("Провайдер", "provider")}</Th>
            <Th>{renderHeader("Регистрация", "created_at")}</Th>
            <Th>{renderHeader("Последняя активность", "last_activity")}</Th>
            <Th>{renderHeader("Статус", "status")}</Th>
            <Th>Действия</Th>
          </tr>
        </thead>
        <tbody>
          {loading && clients.length === 0 && (
            <EmptyRow>
              <td colSpan={7}>Загрузка...</td>
            </EmptyRow>
          )}
          {!loading && clients.length === 0 && (
            <EmptyRow>
              <td colSpan={7}>Нет данных</td>
            </EmptyRow>
          )}
          {clients.map((client) => {
            const status = getClientStatus(client);
            const primaryProvider = getPrimaryProvider(client);
            const birthdate = formatBirthdate(client);
            const age = getAge(client);
            const gender = formatGender(primaryProvider?.gender);

            return (
              <tr key={client.uuid}>
                <Td>
                  <MainText>{getClientName(client)}</MainText>
                  <SubText>ID {client.id} · {formatProviderIdentity(client)}</SubText>
                  {(birthdate || age !== null || gender) && (
                    <SubText>
                      {[birthdate ? `${birthdate}${age !== null ? ` (${age} лет)` : ""}` : null, gender]
                        .filter(Boolean)
                        .join(" · ")}
                    </SubText>
                  )}
                </Td>
                <Td>
                  <ContactLine>
                    <Mail size={13} />
                    {client.email || <Muted>—</Muted>}
                  </ContactLine>
                  <ContactLine>
                    <Phone size={13} />
                    {client.phone_number || <Muted>—</Muted>}
                  </ContactLine>
                  <SubText>Источник: {getContactSource(client)}</SubText>
                </Td>
                <Td>
                  <ProviderList>
                    {client.oauth_providers?.length ? (
                      client.oauth_providers.map((provider) => (
                        <ProviderBadge key={provider.id}>{provider.provider}</ProviderBadge>
                      ))
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </ProviderList>
                  {primaryProvider?.provider_user_id && (
                    <SubText>{primaryProvider.provider_user_id}</SubText>
                  )}
                </Td>
                <Td>
                  <MainText>{moment(client.created_at).format("DD.MM.YYYY")}</MainText>
                  <SubText>{moment(client.created_at).format("HH:mm")}</SubText>
                </Td>
                <Td>
                  {client.last_activity?.last_action_at ? (
                    <>
                      <MainText>
                        {moment(client.last_activity.last_action_at).format("DD.MM.YYYY HH:mm")}
                      </MainText>
                      <SubText>
                        {[client.last_activity.last_ip, client.last_activity.last_country]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </SubText>
                    </>
                  ) : (
                    <Muted>—</Muted>
                  )}
                </Td>
                <Td>
                  <ClientStatusBadge client={client} />
                </Td>
                <Td>
                  {status === "active" && (
                    <>
                      <ActionBtn $color="#e65100" onClick={() => handleBlock(client)}>
                        <Shield size={12} />
                        Блокировать
                      </ActionBtn>
                      <ActionBtn $color="#c62828" onClick={() => handleDelete(client)}>
                        <Trash2 size={12} />
                        Удалить
                      </ActionBtn>
                    </>
                  )}
                  {status === "blocked" && (
                    <>
                      <ActionBtn
                        $color="#2e7d32"
                        onClick={() => unblockClient(client.uuid)}
                      >
                        <ShieldOff size={12} />
                        Разблокировать
                      </ActionBtn>
                      <ActionBtn $color="#c62828" onClick={() => handleDelete(client)}>
                        <Trash2 size={12} />
                        Удалить
                      </ActionBtn>
                    </>
                  )}
                  {status === "deleted" && (
                    <ActionBtn $color="#1976d2" onClick={() => restoreClient(client.uuid)}>
                      <RotateCcw size={12} />
                      Восстановить
                    </ActionBtn>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <PaginationWrap>
        <FooterLeft>
          <span>
            Показано {total === 0 ? 0 : (filter.page - 1) * filter.limit + 1}-
            {Math.min(filter.page * filter.limit, total)} из {total}
          </span>
          <LimitSelect value={filter.limit} onChange={handleLimitChange}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </LimitSelect>
        </FooterLeft>
        {total > filter.limit && (
          <PageButtons>
            <PageBtn disabled={filter.page <= 1} onClick={() => goToPage(filter.page - 1)}>
              &laquo;
            </PageBtn>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let page: number;
              if (totalPages <= 7) {
                page = i + 1;
              } else if (filter.page <= 4) {
                page = i + 1;
              } else if (filter.page >= totalPages - 3) {
                page = totalPages - 6 + i;
              } else {
                page = filter.page - 3 + i;
              }
              return (
                <PageBtn
                  key={page}
                  $active={page === filter.page}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </PageBtn>
              );
            })}
            <PageBtn
              disabled={filter.page >= totalPages}
              onClick={() => goToPage(filter.page + 1)}
            >
              &raquo;
            </PageBtn>
          </PageButtons>
        )}
      </PaginationWrap>
    </TableWrap>
  );
};
